// Reaching a company's chat server and signing in to it (04-sign-in.md, "Reaching a server",
// "Signing in with a password", "Signing in with a token"). Plain HTTP: the chat library is not
// loaded before sign-in.
import { config, entryFor } from '../core/config';
import { fetchVersions } from '../core/server';
import type { Session } from '../core/session';
import { isInstalled } from '../install/install';
import { t } from '../i18n';

export type ReachFailure = 'misconfigured' | 'unreachable' | 'unusable' | 'flows';

export class SignInError extends Error {
  constructor(public readonly reason: string) {
    super(reason);
  }
}

const trim = (url: string) => url.replace(/\/+$/, '');

/** The chat server's base URL: its discovery file, then its versions (an answer without a list fails). */
export async function reach(server: string): Promise<string> {
  let base: string;
  const chat = entryFor(config().desyrSignIn, server)?.chat;
  if (/^https?:\/\//.test(server)) base = trim(server);
  else if (chat) base = trim(chat); // the config's chat server for that name, instead of discovery (03)
  else {
    let origin: string;
    try {
      origin = new URL(`https://${server}`).origin;
    } catch {
      throw new SignInError('unusable');
    }
    base = origin;
    const answer = await fetch(`${origin}/.well-known/matrix/client`).catch(() => undefined);
    if (answer && answer.status !== 404) {
      const body = answer.ok ? await answer.json().catch(() => undefined) : undefined;
      const url = body?.['m.homeserver']?.base_url;
      if (typeof url !== 'string' || !/^https?:\/\//.test(url)) throw new SignInError('misconfigured');
      base = trim(url);
    }
  }
  await fetchVersions(base).catch(() => {
    throw new SignInError('unreachable');
  });
  return base;
}

/** The server's sign-in ways (GET /login). */
export async function loginFlows(base: string): Promise<string[]> {
  const answer = await fetch(`${base}/_matrix/client/v3/login`).catch(() => undefined);
  const body = answer?.ok ? await answer.json().catch(() => undefined) : undefined;
  if (!Array.isArray(body?.flows)) throw new SignInError('flows');
  return body.flows.map((f: { type?: string }) => f.type).filter(Boolean);
}

const LOGIN_TIMEOUT = 30_000;

const DEVICES: [RegExp, string][] = [[/iPhone/, 'iPhone'], [/iPad/, 'iPad'], [/Android/, 'Android'], [/CrOS/, 'Chromebook'], [/Macintosh/, 'Mac'], [/Windows/, 'Windows'], [/Linux/, 'Linux']];
const BROWSERS: [RegExp, string][] = [[/SamsungBrowser/, 'Samsung Internet'], [/OPR\/|Opera/, ''], [/Edg(e|A|iOS)?\//, 'Edge'], [/Firefox|FxiOS/, 'Firefox'], [/Chrome|CriOS/, 'Chrome'], [/Safari/, 'Safari']];
const first = (list: [RegExp, string][], ua: string) => list.find(([test]) => test.test(ua))?.[1] || undefined;

/** The new session's name by device and browser, in the app's language (04, Device names, v1.58.10). */
export function deviceName(ua = navigator.userAgent, installed = isInstalled()): string {
  // An iPad asking for the computer's pages says "Macintosh" but has a touch screen.
  const device = first(DEVICES, ua) === 'Mac' && navigator.maxTouchPoints > 1 ? 'iPad' : first(DEVICES, ua);
  const browser = first(BROWSERS, ua);
  if (device && installed) return t('signin.deviceApp', { device });
  if (device) return t(browser ? 'signin.deviceBrowser' : 'signin.device', { device, browser: browser ?? '' });
  return browser ? t('signin.browser', { browser }) : t('signin.web');
}

/**
 * POST /login with the device name (deviceName; no refresh token asked for). Resolves with the
 * session; a late answer after the 30 s "too long" still resolves (`late` is called with it).
 */
export async function login(base: string, body: Record<string, unknown>, late: (s: Session) => void): Promise<Session> {
  const request = fetch(`${base}/_matrix/client/v3/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, initial_device_display_name: deviceName() }),
  }).then(async (answer) => {
    const json = await answer.json().catch(() => ({}));
    if (!answer.ok) throw new SignInError(json.errcode === 'M_USER_DEACTIVATED' ? 'deactivated' : String(answer.status));
    const { user_id, device_id, access_token, refresh_token } = json;
    if (!user_id || !device_id || !access_token) throw new SignInError('other');
    return { base_url: base, user_id, device_id, access_token, ...(refresh_token ? { refresh_token } : {}) } as Session;
  });
  let timedOut = false;
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => {
      timedOut = true;
      reject(new SignInError('timeout'));
    }, LOGIN_TIMEOUT),
  );
  request.then((s) => timedOut && late(s), () => {});
  return Promise.race([request, timeout]);
}

/** The password form's identifier: something like an address is sent as an email. */
export const identifier = (username: string) =>
  /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(username)
    ? { type: 'm.id.thirdparty', medium: 'email', address: username }
    : { type: 'm.id.user', user: username };

/** 04's words for a refused password or token sign-in; nothing typed is repeated back. */
export function loginWords(error: unknown, token = false): string {
  const reason = error instanceof SignInError ? error.reason : 'network';
  switch (reason) {
    case '400':
      return t('signin.checkTyped');
    case '429':
      return t('signin.waitMoment');
    case 'deactivated':
      return t('signin.deactivated');
    case '403':
      return t(token ? 'signin.linkExpired' : 'signin.wrongUser');
    case 'timeout':
      return t('signin.tooLong');
    default:
      return t('signin.failedMoment');
  }
}

/** 04's words for a server that can't be reached or used. */
export const reachWords = (reason: ReachFailure) =>
  t(({ misconfigured: 'signin.misconfigured', unreachable: 'signin.unreachable', unusable: 'signin.unusable', flows: 'signin.flows' } as const)[reason] ?? 'signin.unreachable');

/** The single sign-on start, returning to this sign-in page's address with the token. */
export const ssoUrl = (base: string, back: string) =>
  `${base}/_matrix/client/v3/login/sso/redirect?redirectUrl=${encodeURIComponent(back)}`;
